import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/Avatar';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MessagesPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    fetchMessages().finally(() => setInitialized(true));
    // Poll every 2 seconds so both buyer and seller see new messages near-real-time
    const interval = setInterval(fetchMessages, 2000);
    return () => clearInterval(interval);
  }, []);

  // Handle deep-linking from listing detail page: /messages?listing_id=X&seller_id=Y
  useEffect(() => {
    if (!initialized) return;
    const listingId = searchParams.get('listing_id');
    const sellerId = searchParams.get('seller_id');
    if (!listingId || !sellerId) return;
    if (selectedConversation && selectedConversation.listing_id === listingId && selectedConversation.other_user_id === sellerId) return;

    // Check for an existing conversation
    const existing = conversations.find(c => c.listing_id === listingId && c.other_user_id === sellerId);
    if (existing) {
      setSelectedConversation(existing);
      return;
    }

    // No existing conversation - fetch seller + listing to build a pending one
    (async () => {
      try {
        const [sellerRes, listingRes] = await Promise.all([
          axios.get(`${API}/users/${sellerId}`),
          axios.get(`${API}/listings/${listingId}`)
        ]);
        setSelectedConversation({
          listing_id: listingId,
          other_user_id: sellerId,
          other_user_name: sellerRes.data.name,
          other_user_picture: sellerRes.data.profile_picture,
          listing_title: listingRes.data.title,
          last_message: '',
          timestamp: new Date().toISOString(),
          unread: false,
          isPending: true
        });
      } catch (error) {
        console.error('Error preparing conversation:', error);
        toast.error('Could not load conversation');
      }
    })();
  }, [searchParams, conversations, initialized, selectedConversation]);

  // Mark messages as read when conversation selected
  useEffect(() => {
    if (!selectedConversation || selectedConversation.isPending) return;
    axios.post(
      `${API}/messages/mark-read`,
      {
        listing_id: selectedConversation.listing_id,
        other_user_id: selectedConversation.other_user_id
      },
      { withCredentials: true }
    ).catch(() => {});
  }, [selectedConversation?.listing_id, selectedConversation?.other_user_id]);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/messages`, { withCredentials: true });
      setMessages(data);
      groupConversations(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const groupConversations = (msgs) => {
    const convMap = new Map();
    msgs.forEach(msg => {
      const otherUserId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
      const key = `${msg.listing_id}-${otherUserId}`;
      const existing = convMap.get(key);
      const otherUserMsg = msgs.find(m => m.sender_id === otherUserId);
      const otherName = otherUserMsg ? otherUserMsg.sender_name : (msg.sender_id === user.id ? 'Recipient' : msg.sender_name);
      if (!existing || new Date(msg.timestamp) > new Date(existing.timestamp)) {
        convMap.set(key, {
          listing_id: msg.listing_id,
          other_user_id: otherUserId,
          other_user_name: otherName,
          last_message: msg.message,
          timestamp: msg.timestamp,
          unread: !msg.read && msg.receiver_id === user.id
        });
      }
    });
    const list = Array.from(convMap.values()).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setConversations(list);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/messages`,
        {
          listing_id: selectedConversation.listing_id,
          receiver_id: selectedConversation.other_user_id,
          message: newMessage
        },
        { withCredentials: true }
      );

      setNewMessage('');
      // Promote pending -> real
      if (selectedConversation.isPending) {
        setSelectedConversation({ ...selectedConversation, isPending: false });
      }
      await fetchMessages();
      toast.success('Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      const detail = error.response?.data?.detail || 'Failed to send message';
      toast.error(typeof detail === 'string' ? detail : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const getConversationMessages = () => {
    if (!selectedConversation) return [];
    return messages.filter(msg =>
      msg.listing_id === selectedConversation.listing_id &&
      (msg.sender_id === selectedConversation.other_user_id || msg.receiver_id === selectedConversation.other_user_id)
    );
  };

  const activeMessages = getConversationMessages();

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1
          className="text-4xl mb-8 tracking-tight"
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
          data-testid="messages-heading"
        >
          Messages
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 250px)' }}>
          <div
            className="md:col-span-1 rounded-md border border-[#E5E0D8] overflow-y-auto"
            style={{ backgroundColor: '#FFFFFF' }}
            data-testid="conversations-list"
          >
            <div className="p-4 border-b border-[#E5E0D8]">
              <h2 style={{ fontFamily: 'Manrope', fontWeight: 600 }}>Conversations</h2>
            </div>
            {conversations.length > 0 ? (
              conversations.map((conv, idx) => (
                <div
                  key={`${conv.listing_id}-${conv.other_user_id}`}
                  onClick={() => setSelectedConversation(conv)}
                  className="p-4 border-b border-[#E5E0D8] cursor-pointer hover:bg-[#F9F6F0] flex items-start gap-3"
                  style={{
                    backgroundColor: selectedConversation?.other_user_id === conv.other_user_id &&
                      selectedConversation?.listing_id === conv.listing_id ? '#F2EFEB' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                  data-testid={`conversation-${idx}`}
                >
                  <Avatar name={conv.other_user_name} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className="truncate"
                        style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '14px' }}
                      >
                        {conv.other_user_name}
                      </span>
                      {conv.unread && (
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#D95A41' }}></div>
                      )}
                    </div>
                    <p
                      className="truncate"
                      style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}
                    >
                      {conv.last_message}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>No conversations yet</p>
              </div>
            )}
          </div>

          <div
            className="md:col-span-2 rounded-md border border-[#E5E0D8] flex flex-col"
            style={{ backgroundColor: '#FFFFFF' }}
            data-testid="active-chat"
          >
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-[#E5E0D8] flex items-center gap-3">
                  <Avatar name={selectedConversation.other_user_name} size={40} />
                  <div>
                    <div style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '16px' }} data-testid="active-chat-name">
                      {selectedConversation.other_user_name}
                    </div>
                    {selectedConversation.listing_title && (
                      <div style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>
                        About: {selectedConversation.listing_title}
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 border-b border-[#E5E0D8]" style={{ backgroundColor: '#FEF3C7' }}>
                  <p style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#92400E' }}>
                    <strong>Tip:</strong> Pay upon pickup and verify food handling practices!
                  </p>
                </div>

                <div className="flex-1 p-6 overflow-y-auto" style={{ backgroundColor: '#F9F6F0' }} data-testid="messages-scroll">
                  {activeMessages.length === 0 ? (
                    <div className="text-center py-8">
                      <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', fontSize: '14px' }}>
                        No messages yet. Say hello to {selectedConversation.other_user_name}!
                      </p>
                    </div>
                  ) : (
                    activeMessages.map((msg, idx) => (
                      <div
                        key={msg.id}
                        className="mb-4 flex"
                        style={{ justifyContent: msg.sender_id === user.id ? 'flex-end' : 'flex-start' }}
                        data-testid={`message-${idx}`}
                      >
                        <div
                          className="max-w-[70%] px-4 py-2 rounded-md"
                          style={{
                            backgroundColor: msg.sender_id === user.id ? '#D95A41' : '#FFFFFF',
                            color: msg.sender_id === user.id ? '#FFFFFF' : '#1A1A1A',
                            border: msg.sender_id === user.id ? 'none' : '1px solid #E5E0D8'
                          }}
                        >
                          <p style={{ fontFamily: 'Manrope', fontSize: '14px' }}>{msg.message}</p>
                          <p
                            className="mt-1"
                            style={{
                              fontFamily: 'Manrope',
                              fontSize: '11px',
                              color: msg.sender_id === user.id ? 'rgba(255,255,255,0.7)' : '#5C5C5C'
                            }}
                          >
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="p-4 border-t border-[#E5E0D8]">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      sendMessage();
                    }}
                    className="flex gap-2"
                  >
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                      style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      disabled={loading || !newMessage.trim()}
                      className="rounded-md"
                      style={{ backgroundColor: '#D95A41' }}
                      data-testid="send-message-btn"
                    >
                      <Send size={18} />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>Select a conversation to start chatting</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
